<template>
  <div v-if="isApiUserValid" class="api-user-tokens-list">
    <Loading
      class="vasion-loading-indicator"
      :active.sync="isLoading"
      :is-full-page="false"
      :color="loaderColor"
      loader="dots"
      :background-color="loaderBackgroundColor"
    />
    <div class="api-user-token-list-container">
      <h1 class="api-user-tokens-list-title"
        >{{ apiUser.userName }} API Tokens</h1
      >
      <div
        v-if="hasApiUserTokens && !isLoading"
        class="vasion-html-table-default"
      >
        <VasionTable
          :headerColumns="tableData.Columns.Values"
          :tableRows="tableData.Rows.Values"
          :hideColumns="hiddenColumns"
          :clickableRows="false"
          @vasion-icon-click="preventAPIUserTokenDeletion"
        />
      </div>
      <div v-else-if="isLoading || !hasApiUserTokens" class="empty-space">
        <p class="token-empty-list-text"
          >There are no tokens generated for this user</p
        >
      </div>
      <VasionButton
        class="ml-0"
        classProp="primary"
        @vasionButtonClicked="generateToken()"
      >
        Generate Token
      </VasionButton>
      <div class="button-group-01">
        <VasionButton classProp="secondary" @vasionButtonClicked="cancel()">
          CANCEL
        </VasionButton>
        <VasionButton classProp="primary" @vasionButtonClicked="done()">
          DONE
        </VasionButton>
      </div>
    </div>
    <!-- Delete API User Token Confirmation Dialog -->
    <VasionGeneralModal
      id="delete-api-user-token"
      modalType="slot"
      :sync="showDeletePreventionModal"
      :hideButtons="true"
    >
      <h1 class="api-user-token-confirm-deletaion-title"
        >Are you sure you want to delete this token?</h1
      >
      <VasionConfirmationDialog
        message="Any applications or scripts using this token will no longer be able to access the Vasion API. You cannot undo this action."
        :yesButtonRed="true"
        yesButtonText="Yes, delete token"
        @noButtonClick="showDeletePreventionModal = false"
        @yesButtonClick="deleteApiUserToken"
      />
    </VasionGeneralModal>
    <VasionSnackbar
      id="snackbar-config-api-user"
      ref="snackbarConfigAPIUser"
      :showSnackbarBool.sync="showSnackbar"
      :snackbarImage="snackbarImage"
      :snackbarSubTitle="snackbarSubTitle"
      :snackbarTitle="snackbarTitle"
    />
  </div>
</template>
<script>
import Loading from 'vue-loading-overlay'
import { loaderBackgroundColor, loaderColor } from '@/assets/js/styleConfig'
import { formatDateTime } from '@/store/helperModules/common.module'

export default {
  name: 'APIUserTokensList',
  components: {
    Loading,
  },
  props: {
    apiUser: {
      type: Object,
      default: () => ({}),
      required: true,
    },
  },
  data: function () {
    return {
      apiUserTokens: [],
      columns: {
        Values: [
          '_ApiTokenID_',
          'Name',
          'CreatedByUserID',
          'Created By',
          'Created Date',
          'Expiration Date',
          '_VasionIconButton_',
        ],
        ValueType: 'String',
      },
      hiddenColumns: ['_ApiTokenID_', 'CreatedByUserID'],
      isLoading: false,
      loaderBackgroundColor: loaderBackgroundColor,
      loaderColor: loaderColor,
      selectedApiUserTokenId: 0,
      showDeletePreventionModal: false,
      showSnackbar: false,
      snackbarImage: false,
      snackbarSubTitle: '',
      snackbarTitle: '',
      tableData: {},
    }
  },
  async created() {
    if (!this.isApiUserValid) {
      this.$emit('cancel')
      return
    }
    await this.loadApiUserTokensList()
  },
  computed: {
    userList() {
      return this.$store.state.security.userList
    },
    hasApiUserTokens() {
      return this.tableData?.Rows?.Values.length > 0
    },
    isApiUserValid() {
      return Object.keys(this.apiUser).length > 0
    },
  },
  methods: {
    async buildTableData(dataValues) {
      const dataList = dataValues.dataList || []

      const data = dataList.map(token => {
        const createdByUser =
          this.userList.find(user => user.UserId === token.CreatedByUserID)
            ?.Username || 'Unknown'
        return {
          Values: [
            token.ApiTokenID,
            token.Name,
            token.CreatedByUserID,
            createdByUser,
            formatDateTime(token.CreatedDate, 'date'),
            formatDateTime(token.ExpiryDate, 'date'),
            'VasionDeleteIcon',
          ],
          ValueType: 'String',
        }
      })

      return {
        Columns: dataValues.columns,
        Rows: {
          Values: data,
          ValueType: 'ApiArray`1',
        },
      }
    },
    cancel() {
      this.$emit('cancel')
    },
    async deleteApiUserToken() {
      if (this.selectedApiUserTokenId <= 0) {
        this.showSnackbarWithMessage('Invalid API User Token ID')
        return
      }

      const saveResult = await this.$store.dispatch(
        'security/deleteApiUserToken',
        this.selectedApiUserTokenId
      )

      if (saveResult?.ResultMessage) {
        this.snackbarTitle = 'Error Deleting API User Token'
        this.snackbarSubTitle = saveResult?.ResultMessage
          ? saveResult.ResultMessage
          : 'Unable to Delete API User Token'
        this.snackbarImage = false
        this.showSnackbar = true
        return
      }

      this.snackbarTitle = 'API User Token Deleted Successfully'
      this.snackbarSubTitle = ''
      this.snackbarImage = true
      this.showSnackbar = true
      this.showDeletePreventionModal = false

      this.loadApiUserTokensList()
    },
    done() {
      this.$emit('cancel')
    },
    async getApiUserTokens() {
      const saveResult = await this.$store.dispatch(
        'security/getApiUserTokens',
        this.apiUser.userID
      )

      if (saveResult?.ResultMessage) {
        this.snackbarTitle = 'Error Getting API User Tokens'
        this.snackbarSubTitle = saveResult.ResultMessage
        this.snackbarImage = false
        this.showSnackbar = true
      } else if (!saveResult || !saveResult?.Data) {
        this.snackbarTitle = 'Error Getting API User Tokens'
        this.snackbarSubTitle = 'Unable to get API User Tokens'
        this.snackbarImage = false
        this.showSnackbar = true
      } else {
        this.apiUserTokens = saveResult.Data
      }
    },
    async loadApiUserTokensList() {
      this.isLoading = true

      await this.getApiUserTokens()

      const data = {
        dataList: this.apiUserTokens,
        columns: this.columns,
      }

      this.tableData = await this.buildTableData(data)

      this.isLoading = false
    },
    generateToken() {
      this.$emit('open-generate-token-form', this.apiUser)
    },
    preventAPIUserTokenDeletion(rowIndex) {
      const selectedApiUserTokenId = rowIndex?.item?.Values[0]
      if (selectedApiUserTokenId)
        this.selectedApiUserTokenId = selectedApiUserTokenId
      else return

      this.showDeletePreventionModal = true
    },
    showSnackbarWithMessage(message) {
      this.snackbarTitle = 'API User Configuration'
      this.snackbarSubTitle = message
      this.snackbarImage = false
      this.showSnackbar = true
    },
  },
}
</script>
<style lang="scss" scoped>
@import '@/assets/css/variables.scss';
@import '@/assets/css/light-theme-system-palette.scss';

.api-user-tokens-list {
  position: relative;
}

::v-deep .vasion-loading-indicator {
  height: unset;
}

.api-user-token-list-container {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 8px;
  flex-shrink: 0;
  width: 100%;

  @media (min-width: $breakpoint-sm) {
    max-width: 500px;
  }

  @media (min-width: $breakpoint-md) {
    max-width: 700px;
  }

  @media (min-width: $breakpoint-lg) {
    max-width: 900px;
  }

  @media (min-width: $breakpoint-xl) {
    max-width: 900px;
  }
}

.api-user-tokens-list-title {
  width: 100%;
  text-align: center;
  color: $on-surface-variant;
  font-family: $font-family-book;
  font-size: 32px;
  font-style: normal;
  font-weight: 400;
  line-height: 40px;
  margin: 0px;
}

.api-user-token-list-container .vasion-html-table-default {
  width: 100%;
  height: 300px;
  overflow-y: auto;
}

::v-deep .ml-0 button {
  margin-left: 0 !important;
}

.api-user-token-confirm-deletaion-title {
  color: $on-surface-container;
  font-family: $font-family-book;
  font-size: 24px;
  font-style: normal;
  font-weight: 400;
  line-height: 32px;
}
.api-user-token-confirm-deletaion-text {
  color: $on-surface-container;
  font-family: $font-family-book;
  font-size: 14px;
  font-style: normal;
  font-weight: 400;
  line-height: 20px;
  letter-spacing: 0.25px;
}

.empty-space {
  width: 733px;
  height: 300px;
  text-align: center;
  padding-top: 122px;
}

.token-empty-list-text {
  color: $on-surface;
  text-align: center;
  font-family: $font-family-book;
  font-size: 24px;
  font-style: normal;
  font-weight: 400;
  line-height: 32px;
  margin: unset;
}
</style>
